import { z } from 'zod'

import {
  accept,
  isPlayersTurn,
  nextPlayerId,
  reject,
  type ActionContext,
  type GameEngine,
  type PlaySummary,
  type Player,
  type TurnState,
} from '../engine.js'
import type { Rng } from '../rng.js'
import { applyAnswer, availableSkills, chooseFrom, FLUENT_MS } from './maths-adaptive.js'
import { levelOf, overallLevel, type Question, type SkillLevels } from './maths-skills.js'

/** Questions to get right before a round is done. */
export const DEFAULT_ROUND_LENGTH = 10

/**
 * How many answers a round allows per question before giving up on it.
 *
 * Wrong answers come back, which without a bound means a round could run
 * forever on a question somebody cannot get. Reaching the cap ends the round
 * kindly rather than trapping her in it.
 *
 * Scaled off the round length rather than fixed: a hardcoded ceiling would
 * make a longer round unwinnable, since the cap would trip before the last
 * questions were reached.
 */
export const ATTEMPTS_PER_QUESTION = 4

/**
 * How many goes at one question before the answer is shown.
 *
 * Being told "not quite, try again" is how somebody finds an answer
 * themselves, which is worth more than being handed it. Three rather than
 * unlimited so that a question she genuinely cannot do ends kindly instead of
 * becoming a wall.
 */
export const TRIES_PER_QUESTION = 3

export function attemptCap(roundLength: number): number {
  return roundLength * ATTEMPTS_PER_QUESTION
}

export interface MathsConfig {
  readonly levels?: SkillLevels
  readonly roundLength?: number
  /**
   * When the round was dealt.
   *
   * Required, not optional. Fluency is judged against this, and defaulting it
   * to zero silently made the first question of every round look like it took
   * since 1970 — so it never counted as fluent however fast it was answered.
   * Making callers pass a clock is the only version of this that cannot rot.
   */
  readonly startedAt: number
}

export interface MathsAttempt {
  readonly skillKey: string
  readonly prompt: string
  readonly expected: number
  readonly given: number
  readonly correct: boolean
  readonly elapsedMs: number
  readonly playerId: string
}

export interface MathsState {
  /** Still to get right. The head is the current question. */
  readonly queue: readonly Question[]
  readonly levels: SkillLevels
  readonly roundLength: number
  readonly attempts: readonly MathsAttempt[]
  /** Shown after an answer, so a wrong one teaches rather than just failing. */
  readonly lastAnswer: MathsAttempt | null
  /** Whether that answer left its question open for another go. */
  readonly lastAnswerHadMoreGoes: boolean
  readonly correctCount: number
  readonly status: 'playing' | 'finished'
  readonly turn: TurnState
  readonly startedAt: number
  /** When the current question was put up, for judging fluency. */
  readonly askedAt: number
  /**
   * Goes taken at the question currently in front of her.
   *
   * In the state rather than in the browser, because "you have another try" is
   * a rule: a room has to agree with a phone about whether the question is
   * still open, and a client deciding for itself could hand out a fourth.
   */
  readonly triesOnCurrent: number
}

export const mathsAction = z.object({
  type: z.literal('answer'),
  // Whole numbers only: every question is built to have one.
  value: z.number().int().min(-9999).max(999_999),
})

export type MathsAction = z.infer<typeof mathsAction>

export interface MathsView {
  readonly prompt: string | null
  /** How many she has got right. The round ends when this reaches
   *  roundLength, since a wrong answer goes back in the queue. */
  readonly correct: number
  readonly roundLength: number
  readonly status: MathsState['status']
  readonly currentPlayerId: string | null
  /**
   * The previous answer, so the right one can be shown before moving on.
   *
   * `moreGoes` says whether that answer left the question open. It has to
   * travel with the answer rather than being read off `triesLeft`: by the time
   * a client sees this, `triesLeft` is about whatever question is on screen
   * now — which after a third wrong answer is the next one, with a full three
   * goes, and reading it would tell her to try again at something she has
   * already been shown the answer to.
   */
  readonly lastAnswer:
    | (Pick<MathsAttempt, 'prompt' | 'expected' | 'given' | 'correct'> & {
        readonly moreGoes: boolean
      })
    | null
  /**
   * Goes left at the question on screen, so the browser can say "try again"
   * rather than working out for itself whether it is allowed to.
   */
  readonly triesLeft: number
  /** Wrong answers so far this round, which is what Daddy's lane counts. */
  readonly wrongAnswers: number
}

/**
 * What a client is allowed to see.
 *
 * The queue never leaves the server, so the answer to the current question
 * cannot be read out of a socket payload. Unlike Hangman there is no single
 * secret word, but the effect is the same.
 */
export function toMathsView(state: MathsState): MathsView {
  const current = state.queue[0]

  return {
    prompt: current?.prompt ?? null,
    correct: state.correctCount,
    roundLength: state.roundLength,
    status: state.status,
    currentPlayerId: state.turn.currentPlayerId,
    triesLeft: Math.max(0, TRIES_PER_QUESTION - state.triesOnCurrent),
    // Counted from the attempts rather than kept separately, so it cannot
    // drift from the answers actually given.
    wrongAnswers: state.attempts.filter((attempt) => !attempt.correct).length,
    lastAnswer: state.lastAnswer
      ? {
          prompt: state.lastAnswer.prompt,
          expected: state.lastAnswer.expected,
          given: state.lastAnswer.given,
          correct: state.lastAnswer.correct,
          moreGoes: state.lastAnswerHadMoreGoes,
        }
      : null,
  }
}

/**
 * Puts a missed question back, a few places later.
 *
 * Not immediately — repeating it straight away tests short-term memory rather
 * than the fact. Far enough to have thought about something else, near enough
 * to still be the same session.
 */
function requeue(queue: readonly Question[], question: Question): readonly Question[] {
  const rest = queue.slice(1)
  const at = Math.min(3, rest.length)
  return [...rest.slice(0, at), question, ...rest.slice(at)]
}

export const mathsEngine: GameEngine<MathsConfig, MathsState, MathsAction> = {
  key: 'maths',
  actionSchema: mathsAction,

  createInitialState(config: MathsConfig, players: readonly Player[], rng: Rng): MathsState {
    const levels = config.levels ?? {}
    const roundLength = config.roundLength ?? DEFAULT_ROUND_LENGTH

    // The whole round is dealt up front, so the reducer needs no randomness of
    // its own and a round replays exactly from its seed.
    //
    // What is available is decided once, from where the player actually stands.
    // Letting the walk-forward below re-open that door meant a lucky run while
    // dealing could unlock material a beginner has never met — measured at
    // roughly one first-ever round in seven hundred reaching times tables, and
    // occasionally fractions.
    const unlocked = availableSkills(levels)

    const queue: Question[] = []
    let working = levels
    for (let i = 0; i < roundLength; i++) {
      const question = chooseFrom(unlocked, working, rng).build(rng)
      queue.push(question)
      // The walk-forward still shifts the weighting, so one round covers a
      // spread rather than asking the same weak skill ten times — it just no
      // longer changes what is on the menu.
      working = applyAnswer(working, question.skillKey, true, 0)
    }

    return {
      queue,
      levels,
      roundLength,
      attempts: [],
      lastAnswer: null,
      lastAnswerHadMoreGoes: false,
      correctCount: 0,
      status: 'playing',
      turn: { players, currentPlayerId: players[0]?.id ?? null },
      startedAt: config.startedAt,
      askedAt: config.startedAt,
      triesOnCurrent: 0,
    }
  },

  reduce(state, action, context: ActionContext) {
    if (state.status !== 'playing') return reject(state, 'game-over')
    if (!isPlayersTurn(state.turn, context.playerId)) return reject(state, 'not-your-turn')

    const current = state.queue[0]
    if (!current) return reject(state, 'game-over')

    const elapsedMs = Math.max(0, context.now - state.askedAt)
    const correct = action.value === current.answer

    const attempt: MathsAttempt = {
      skillKey: current.skillKey,
      prompt: current.prompt,
      expected: current.answer,
      given: action.value,
      correct,
      elapsedMs,
      playerId: context.playerId,
    }

    const attempts = [...state.attempts, attempt]
    const levels = applyAnswer(state.levels, current.skillKey, correct, elapsedMs)

    const tries = correct ? 0 : state.triesOnCurrent + 1
    // Another go, until the third. The question stays at the head of the queue
    // and the clock restarts, so a second attempt is timed as a second attempt
    // rather than inheriting the thinking that went before it.
    const tryAgain = !correct && tries < TRIES_PER_QUESTION

    // A wrong answer costs no life and ends nothing. Once the goes are used up
    // the right answer is shown and the question comes back later, because the
    // point is learning the fact rather than surviving it.
    const queue = correct
      ? state.queue.slice(1)
      : tryAgain
        ? state.queue
        : requeue(state.queue, current)

    const correctCount = correct ? state.correctCount + 1 : state.correctCount

    const finished = queue.length === 0 || attempts.length >= attemptCap(state.roundLength)

    return accept({
      ...state,
      queue,
      levels,
      attempts,
      lastAnswer: attempt,
      lastAnswerHadMoreGoes: tryAgain,
      correctCount,
      status: finished ? 'finished' : 'playing',
      askedAt: context.now,
      triesOnCurrent: tryAgain ? tries : 0,
      // The turn passes on every answer, right or wrong. Without this the
      // first player holds it forever and everybody else is refused for the
      // whole game — which is what happened, because the turn tests only
      // checked that a non-current player was rejected.
      turn: { ...state.turn, currentPlayerId: nextPlayerId(state.turn) },
    })
  },

  isTerminal: (state) => state.status !== 'playing',

  summarise(state, endedAt): PlaySummary {
    const durationMs = Math.max(0, endedAt - state.startedAt)

    return {
      gameKey: 'maths',
      result: state.correctCount >= state.roundLength ? 'won' : 'abandoned',
      durationMs,
      difficulty: `level ${Math.round(overallLevel(state.levels))}`,
    }
  },
}

/**
 * The levels this round actually changed.
 *
 * State carries every skill the player has ever recorded, so saving it whole
 * would re-write skills this round never touched — quietly reverting anything
 * another device changed in the meantime. Only what was practised should be
 * written back.
 */
export function levelsTouched(state: MathsState): SkillLevels {
  const touched = new Set(state.attempts.map((attempt) => attempt.skillKey))

  return Object.fromEntries(
    [...touched].map((skillKey) => [skillKey, levelOf(state.levels, skillKey)]),
  )
}

export { FLUENT_MS }
